import cx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "ttag";

import CS from "metabase/css/core/index.css";
import { copyData } from "metabase/jerry/Common/copy-to-clipboard";
import { trackEvent } from "metabase/jerry/Common/jerry-utils";
import { useSelector } from "metabase/lib/redux";
import { PLUGIN_FEATURE_LEVEL_PERMISSIONS } from "metabase/plugins";
import {
  getFirstQueryResult,
  getQuestion,
  getRawSeries,
  getUiControls,
  getVisualizationSettings,
} from "metabase/query_builder/selectors";
import { getUser } from "metabase/selectors/user";
import { Icon } from "metabase/ui";
import type { Dataset } from "metabase-types/api";

export const CopyData = () => {
  // State to track if the data was just copied.
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  // Selectors to get data from the Redux store.
  const { isShowingRawTable } = useSelector(getUiControls);
  const rawSeries = useSelector(getRawSeries);
  const result = useSelector(getFirstQueryResult);
  const visualizationSettings = useSelector(getVisualizationSettings);
  const question = useSelector(getQuestion);
  const user = useSelector(getUser);

  // The click handler, wrapped in useCallback for performance.
  const handleCopy = useCallback(() => {
    if (!result) {
      return;
    }
    const dataToCopy = copyData(
      isShowingRawTable,
      result,
      visualizationSettings,
      rawSeries,
    );

    try {
      navigator.clipboard.writeText(dataToCopy);
      // eslint-disable-next-line no-console
      console.log("Data copied to clipboard successfully!");

      setIsCopied(true);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = window.setTimeout(() => {
        setIsCopied(false);
      }, 2000);

      trackEvent(
        {
          eventLabel: "Copied",
        },
        {
          user_info: user,
          question: question?.card(),
        },
      );
    } catch (err) {
      console.error("Failed to copy data: ", err);
    }
  }, [
    isShowingRawTable,
    question,
    rawSeries,
    result,
    user,
    visualizationSettings,
  ]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Icon
      name={isCopied ? "check" : "copy"}
      key="copy-data-to-clipboard-icon"
      tooltip={t`Copy data to clipboard`}
      className={cx(
        "copy data button",
        "mx1 text-brand-hover",
        CS.cursorPointer,
      )}
      onClick={handleCopy}
    />
  );
};

interface ShouldRenderCopyDataProps {
  result?: Dataset;
}

CopyData.shouldRender = ({ result }: ShouldRenderCopyDataProps) => {
  return (
    result &&
    !result.error &&
    PLUGIN_FEATURE_LEVEL_PERMISSIONS.canDownloadResults(result)
  );
};
